import { createCRUDHook } from './createCRUDHook';
import {
  PartChangeLogDto,
  PartList,
  PartsDto,
} from 'utils/models';
import { FilterOptionModel, JsonPatch } from 'utils/models/common.model';
import {
  CHECK_PART_NUMBER_EXIST,
  FILTERED_ENTITIES,
  GET_DISTINCT_PART_CATEGORIES,
  GET_FILTERED_PART,
  GET_PART_DETAIL_BY_STAFF_ID,
  TBL_PARTS,
  TBL_PART_CHANGE_LOG,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';
import { AxiosResponse } from 'axios';
import { PartFilterOptionsModel } from 'utils/models/PartFilterOption.model';

interface PartCustomOperations {
  patchUpdatePartData: (
    partId: number,
    param: JsonPatch[]
  ) => Promise<AxiosResponse>;
  checkPartNumberExist: (id: string) => Promise<boolean>;
  getFilteredPartListData: (
    filteredEntities: PartFilterOptionsModel
  ) => Promise<PartList>;
  getChangeLogDataByPartId: (
    filteredEntities: FilterOptionModel
  ) => Promise<any>;
  postChangeLogData: (data: PartChangeLogDto) => Promise<PartChangeLogDto>;
  GetDistinctPartCategories: () => Promise<string[]>;
  getPartsDataByStaffId: (id: number) => Promise<PartList>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  PartsDto,
  PartList,
  PartCustomOperations
>({
  entityName: 'Part',
  endpoint: TBL_PARTS,

  customOperations: (auth) => ({
    patchUpdatePartData: async (
      partId: number,
      param: JsonPatch[]
    ): Promise<AxiosResponse> => {
      try {
        let res = await auth.makeAuthenticatedPatch(
          TBL_PARTS + `/${partId}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    checkPartNumberExist: async (id: string): Promise<boolean> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PARTS + CHECK_PART_NUMBER_EXIST + `?partNumber=${id}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getFilteredPartListData: async (
      filteredEntities: PartFilterOptionsModel
    ): Promise<PartList> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PARTS +
            GET_FILTERED_PART +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getChangeLogDataByPartId: async (
      filteredEntities: FilterOptionModel
    ): Promise<any> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PART_CHANGE_LOG +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    postChangeLogData: async (
      data: PartChangeLogDto
    ): Promise<PartChangeLogDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(TBL_PART_CHANGE_LOG, data);
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    GetDistinctPartCategories: async (): Promise<string[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PARTS + GET_DISTINCT_PART_CATEGORIES
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getPartsDataByStaffId: async (staffId: number): Promise<PartList> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_PARTS + GET_PART_DETAIL_BY_STAFF_ID + `?staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const PartContext = Context;
export const usePartManager = useManager;
export const usePart = useConsumer;
